<template>
  <div>
    <!-- MODO NORMAL: exibe formulário e botão para iniciar “Executar” -->
    <div v-if="!props.printMode">
      <h6>{{ props.printTitle }}</h6><p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicio" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFim" class="form-control" />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO IMPRESSÃO: busca automática e exibe tabela + total -->
    <div v-else>
      <p class="text-center" style="font-size: 14px;">
        ( {{ globalUtils.formatarData(dataInicio) }} a {{ globalUtils.formatarData(dataFim) }} )
      </p>
      <div v-if="loading" class="mb-3">
        Carregando…
      </div>

      <!-- RESULTADOS (quando houver) -->
      <div v-else-if="resultados.length">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>REFIL</th>
              <th>DESCRIÇÃO</th>
              <th class="valor-cell">VALOR</th>
              <th class="text-center">FORMA DE PAGAMENTO</th>
              <th class="text-center">DATA VENCIMENTO</th>
              <th class="text-center">DATA PAGAMENTO</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, idx) in resultados"
              :key="item.data_pagamento + '-' + item.descricao + '-' + idx"
            >
              <td>{{ item.refil }}</td>
              <td>{{ item.descricao }}</td>
              <td class="valor-cell">{{ formatarMoeda(item.valor) }}</td>
              <td class="text-center">{{ item.forma_pagamento }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_vencimento) }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_pagamento) }}</td>
            </tr>
          </tbody>
          <!-- RODAPÉ COM TOTAL -->
          <tfoot>
            <tr>
              <td colspan="2"><strong>Total</strong></td>
              <td class="valor-cell text-right">
                <strong>{{ formatarMoeda(total) }}</strong>
              </td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Mensagem caso não encontre nenhum registro -->
      <div v-else-if="!loading && executed">
        <p>Nenhuma despesa paga encontrada no intervalo selecionado.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'


// Props vindas da rota de impressão
const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: { type: String, default: 'Despesas Pagas por Pagamento' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()
const route  = useRoute()

// Estados para parâmetros (modo normal)
const dataInicio = ref('')
const dataFim    = ref('')

// Estados para controle de busca (modo print)
const loading    = ref(false)
const resultados = ref([])
const total      = ref(0)    // ← guarda a soma de todos os valores
const executed   = ref(false) // indica que já tentamos buscar (mesmo que não tenha resultado)

/**
 * Função que faz a requisição ao back-end
 * Consumindo desessas-pagas-por-pagamento?dataInicio=&dataFim=
 * e armazenando em resultados.value e total.value
 */
async function buscarDespesas() {
  if (!dataInicio.value || !dataFim.value) {
    resultados.value = []
    total.value = 0
    executed.value = true
    return
  }

  loading.value    = true
  resultados.value = []
  total.value      = 0
  executed.value   = false

  try {
    const res = await axios.get(
      '/gestao/relatorios/despesas/despesas-pagas-por-pagamento',
      {
        params: {
          dataInicio: dataInicio.value,
          dataFim:    dataFim.value
        }
      }
    )
    // O back-end devolve { items: [...], total: 1234.56 }
    resultados.value = Array.isArray(res.data.items) ? res.data.items : []
    total.value      = parseFloat(res.data.total) || 0
  } catch (err) {
    console.error('Erro ao buscar despesas pagas por pagamento:', err)
    resultados.value = []
    total.value      = 0
  } finally {
    loading.value  = false
    executed.value = true
  }
}

/**
 * Abre a rota de impressão, passando title, dataInicio e dataFim via query params
 */
function abrirPrint() {
  if (!dataInicio.value || !dataFim.value) return

  const inicioFormatado = globalUtils.formatarData(dataInicio.value)
  const fimFormatado    = globalUtils.formatarData(dataFim.value)

  // 3) Montar o subtitle completo, incluindo período
  const subtitle = `( ${inicioFormatado} a ${fimFormatado} )`

  router.push({
    path: `/gestao/relatorios/print/despesas-pagas-por-pagamento`,
    query: {
      title:      props.printTitle,
      dataInicio: dataInicio.value,
      dataFim:    dataFim.value,
      subtitle:   subtitle
    }
  })
}

// /// SE ESTIVER EM PRINT MODE, executa a busca na montagem ///
onMounted(() => {
  if (!props.printMode) return

  // Pega os valores de dataInicio e dataFim vindos por query params
  dataInicio.value = route.query.dataInicio || ''
  dataFim.value    = route.query.dataFim    || ''

  // Chama buscarDespesas() imediatamente
  buscarDespesas()
})

/**
 * Funções utilitárias para formatar data e moeda
 */
function formatarMoeda(num) {
  if (num == null) return ''
  return Number(num).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.valor-cell {
  text-align: right !important;
}
.text-right {
  text-align: right !important;
}
</style>
